/**
 * Send several people's documents to a customer.
 *
 * Before a technician goes on site the customer asks for their paperwork, and often
 * the company's DURC and visura too. Three choices — who, which documents, to whom —
 * and a live summary of what will go and what is missing, because the thing a customer
 * notices is the certificate that is not there.
 *
 * Two ways out: an email straight from the system, or a ZIP to attach from the office's
 * own mailbox. The ZIP always works; the email needs AWS to have approved sending to
 * outside addresses, and says so plainly until it has.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Field, Spinner } from '@/components/ui';
import { PeopleMultiSelect } from '@/features/access/people';
import { useI18n } from '@/i18n';
import { api, ApiError, type DocumentType, type PackSelection } from '@/lib/api';
import { fileSize } from '@/lib/format';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Waits for the selection to settle before asking the server what it adds up to. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

export function SendDocumentsDialog({
  open,
  onClose,
  initialUserIds = [],
}: {
  open: boolean;
  onClose: () => void;
  initialUserIds?: string[];
}) {
  const { t } = useI18n();

  const [userIds, setUserIds] = useState<string[]>(initialUserIds);
  const [typeCodes, setTypeCodes] = useState<string[]>([]);
  const [companyCodes, setCompanyCodes] = useState<string[]>([]);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [copyToMe, setCopyToMe] = useState(true);
  const [done, setDone] = useState<string | null>(null);

  // A fresh start each time it opens, with whoever it was opened for.
  useEffect(() => {
    if (!open) return;
    setUserIds(initialUserIds);
    setTypeCodes([]);
    setCompanyCodes([]);
    setTo('');
    setSubject(t('send.defaultSubject'));
    setMessage(t('send.defaultMessage'));
    setCopyToMe(true);
    setDone(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users(), enabled: open });
  const types = useQuery({ queryKey: ['document-types'], queryFn: () => api.documentTypes(), enabled: open });
  const status = useQuery({ queryKey: ['pack-status'], queryFn: api.packStatus, enabled: open });

  const workerTypes = (types.data?.types ?? []).filter((x) => x.scope === 'worker');
  const companyTypes = (types.data?.types ?? []).filter((x) => x.scope === 'company');

  const selection: PackSelection = useMemo(
    () => ({ userIds, typeCodes, companyTypeCodes: companyCodes }),
    [userIds, typeCodes, companyCodes],
  );
  const settled = useDebounced(selection, 350);
  const hasSelection =
    (settled.userIds.length > 0 && settled.typeCodes.length > 0) || settled.companyTypeCodes.length > 0;

  const preview = useQuery({
    queryKey: ['pack-preview', settled],
    queryFn: () => api.packPreview(settled),
    enabled: open && hasSelection,
  });

  const recipients = to
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const recipientsValid = recipients.length > 0 && recipients.length <= 5 && recipients.every((r) => EMAIL.test(r));

  const fileCount = hasSelection ? (preview.data?.files.length ?? 0) : 0;
  const totalBytes = hasSelection ? (preview.data?.totalBytes ?? 0) : 0;
  const emailAvailable = status.data?.emailAvailable ?? false;
  const tooBigForEmail = status.data ? totalBytes > status.data.maxEmailBytes : false;

  const zip = useMutation({
    mutationFn: () => api.packZip(selection),
    onSuccess: (r) => {
      window.location.href = r.url;
      setDone(t('send.zipReady', { count: r.files, size: fileSize(r.sizeBytes) }));
    },
  });

  const email = useMutation({
    mutationFn: () => api.packEmail({ ...selection, to: recipients, copyToMe, subject, message }),
    onSuccess: (r) => setDone(t('send.sent', { count: r.sent, to: recipients.join(', ') })),
  });

  const busy = zip.isPending || email.isPending;
  const error = email.error ?? zip.error;

  return (
    <Modal
      open={open}
      onClose={() => (busy ? undefined : onClose())}
      size="lg"
      title={t('send.title')}
      description={t('send.hint')}
      footer={
        done ? (
          <button type="button" className="btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!emailAvailable || !recipientsValid || fileCount === 0 || tooBigForEmail || busy}
              onClick={() => email.mutate()}
            >
              {email.isPending ? t('send.sending') : t('send.sendEmail')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={fileCount === 0 || busy}
              onClick={() => zip.mutate()}
            >
              {zip.isPending ? t('send.preparing') : t('send.downloadZip')}
            </button>
            <button type="button" className="btn-ghost" disabled={busy} onClick={onClose}>
              {t('common.cancel')}
            </button>
          </>
        )
      }
    >
      {done ? (
        <p className="py-6 text-center text-sm">{done}</p>
      ) : (
        <div className="space-y-6">
          {/* 1. Who */}
          <section>
            <StepTitle n={1} title={t('send.who')} hint={t('send.selectedCount', { count: userIds.length })} />
            {users.isLoading ? (
              <Spinner />
            ) : (
              <div className="max-h-56 overflow-y-auto rounded border border-border">
                <PeopleMultiSelect users={users.data?.users ?? []} selected={userIds} onChange={setUserIds} />
              </div>
            )}
          </section>

          {/* 2. Which documents */}
          <section>
            <StepTitle n={2} title={t('send.which')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TypeChecklist
                title={t('send.employeeDocuments')}
                types={workerTypes}
                selected={typeCodes}
                onChange={setTypeCodes}
              />
              <TypeChecklist
                title={t('send.companyDocuments')}
                types={companyTypes}
                selected={companyCodes}
                onChange={setCompanyCodes}
              />
            </div>
          </section>

          {/* What that adds up to */}
          {hasSelection ? (
            <section className="rounded-lg border border-border bg-surface-alt px-4 py-3 space-y-2">
              {preview.isLoading ? (
                <Spinner />
              ) : (
                <>
                  <div className="text-sm font-semibold">
                    {fileCount === 1
                      ? t('send.summaryOne', { size: fileSize(totalBytes) })
                      : t('send.summary', { count: fileCount, size: fileSize(totalBytes) })}
                  </div>
                  {preview.data && preview.data.files.length > 0 ? (
                    <details className="text-xs text-muted">
                      <summary className="cursor-pointer hover:text-primary">{t('send.showFiles')}</summary>
                      <ul className="mt-1 space-y-0.5">
                        {preview.data.files.map((f) => (
                          <li key={f.id} className="truncate">
                            {f.holder ?? 'HH Electrical Systems'} · {f.typeLabel} · {f.filename}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                  {preview.data && preview.data.missing.length > 0 ? (
                    <div className="text-xs">
                      <span className="font-semibold text-warning">
                        {t('send.missing', { count: preview.data.missing.length })}
                      </span>{' '}
                      <span className="text-muted">
                        {preview.data.missing.map((m) => `${m.holder} (${m.typeLabel})`).join(', ')}
                      </span>
                    </div>
                  ) : null}
                  {tooBigForEmail ? <p className="text-xs text-warning">{t('send.tooBig')}</p> : null}
                </>
              )}
            </section>
          ) : null}

          {/* 3. To whom */}
          <section className="space-y-3">
            <StepTitle n={3} title={t('send.toWhom')} />
            {status.data && !emailAvailable ? (
              <p className="rounded-lg bg-warning-soft px-3 py-2.5 text-xs">{t('send.emailPending')}</p>
            ) : null}
            <Field label={t('send.recipients')} hint={t('send.recipientsHint')}>
              <input
                className="input"
                type="text"
                inputMode="email"
                value={to}
                placeholder="cantiere@cliente.it"
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
            <Field label={t('send.subject')}>
              <input className="input" maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Field>
            <Field label={t('send.message')}>
              <textarea
                className="input h-28 py-2"
                maxLength={5000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={copyToMe} onChange={(e) => setCopyToMe(e.target.checked)} />
              {t('send.copyToMe')}
            </label>
            <p className="text-xs text-muted">{t('send.privacy')}</p>
          </section>

          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error instanceof ApiError ? error.message : t('common.unknownError')}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function StepTitle({ n, title, hint }: { n: number; title: string; hint?: string }) {
  return (
    <div className="mb-2 flex items-baseline gap-2">
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-soft)] text-[11px] font-bold text-primary">
        {n}
      </span>
      <h3 className="text-sm font-bold">{title}</h3>
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </div>
  );
}

function TypeChecklist({
  title,
  types,
  selected,
  onChange,
}: {
  title: string;
  types: DocumentType[];
  selected: string[];
  onChange: (codes: string[]) => void;
}) {
  const { t } = useI18n();
  const all = types.length > 0 && types.every((x) => selected.includes(x.code));
  const toggle = (code: string) =>
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);

  return (
    <div className="rounded border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-subtle">{title}</span>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => onChange(all ? [] : types.map((x) => x.code))}
        >
          {all ? t('send.none') : t('send.all')}
        </button>
      </div>
      <ul className="max-h-48 overflow-y-auto py-1">
        {types.map((x) => (
          <li key={x.code}>
            <label
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-surface-alt"
              style={{ paddingInlineStart: x.parent_code ? 28 : 12 }}
            >
              <input type="checkbox" checked={selected.includes(x.code)} onChange={() => toggle(x.code)} />
              <span className="truncate">{x.label_it}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
